import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination, Select } from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { exportSaleReturnsToPDF } from "../../utils/exportSaleReturn";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

function Row({ row }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const itemCount = row.return_items?.length ?? 0;

  const currency = row.sale_details
    ? Object.keys(row.sale_details.total_amount || {})[0] || ""
    : "";

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>{row.sale_details?.id ? `Sale #${row.sale_details.id}` : "-"}</TableCell>
        <TableCell>{row.return_date?.slice(0, 10) ?? "-"}</TableCell>
        <TableCell>{row.reason ?? "-"}</TableCell>
        <TableCell>
          {firstCurrencyValue(row.sale_details?.total_amount)} {currency}
        </TableCell>
        <TableCell>
          {row.total_refound_amount ?? "-"} {currency}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.returnSale_collapse_title")}
              </Typography>

              <Table size="small" aria-label="return-items">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_product")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_qty")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_price")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_refundAmount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.return_items?.map((item) => {
                    const salePrice = firstCurrencyValue(
                      item.sale_item_details?.sale_price
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell component="th" scope="row">
                          {item.sale_item_details?.product?.name}
                        </TableCell>
                        <TableCell>{item.return_quantity}</TableCell>
                        <TableCell>{salePrice}</TableCell>
                        <TableCell>{item.refund_amount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    sale_details: PropTypes.object,
    return_date: PropTypes.string,
    reason: PropTypes.string,
    total_refound_amount: PropTypes.string,
    return_items: PropTypes.array,
  }).isRequired,
};

export default function ReturnTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedSaleId, setSelectedSaleId] = React.useState("");
  const [selectedCustomer, setSelectedCustomer] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    params.push(`page=${page}`);
    if (rowsPerPage !== -1) {
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [startDate, endDate, page, rowsPerPage]);

  const {
    data: returnsData,
    isLoading,
    isError,
    error,
  } = useFetchObjects(
    ["sale-returns", queryString, selectedSaleId, selectedCustomer],
    `shop/sale-returns/?${queryString}`,
    token
  );

  // Debug logging
  React.useEffect(() => {
    console.log('API Response:', returnsData);
    console.log('Token:', token);
    console.log('Query String:', queryString);
    console.log('Error:', error);
  }, [returnsData, token, queryString, error]);

  // Fetch customers for filter dropdown
  const { data: customersData } = useFetchObjects(
    ["customers"],
    "shop/customers",
    token
  );

  const customers = customersData?.results || [];

  const returns = returnsData?.results || returnsData || [];
  const totalCount = returnsData?.count || returns.length || 0;

  // Debug: Show data structure
  React.useEffect(() => {
    if (returns.length > 0) {
      console.log('First return item structure:', returns[0]);
    }
  }, [returns]);

  // Filter by sale ID and customer locally
  const filteredReturns = React.useMemo(() => {
    let filtered = returns;
    if (selectedSaleId) {
      filtered = filtered.filter(ret => 
        ret.sale_details?.id?.toString().includes(selectedSaleId)
      );
    }
    if (selectedCustomer) {
      filtered = filtered.filter(ret => 
        ret.sale_details?.customer_details?.name?.toLowerCase().includes(selectedCustomer.toLowerCase())
      );
    }
    return filtered;
  }, [returns, selectedSaleId, selectedCustomer]);

  const finalCount = filteredReturns.length;
  const displayReturns = React.useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredReturns;
    }
    const startIndex = (page - 1) * rowsPerPage;
    return filteredReturns.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredReturns, page, rowsPerPage]);

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return displayReturns.reduce((acc, ret) => {
      const saleTotal = parseFloat(firstCurrencyValue(ret.sale_details?.total_amount)) || 0;
      const refundTotal = parseFloat(ret.total_refound_amount) || 0;
      
      return {
        totalSaleAmount: acc.totalSaleAmount + saleTotal,
        totalRefundAmount: acc.totalRefundAmount + refundTotal
      };
    }, { totalSaleAmount: 0, totalRefundAmount: 0 });
  }, [displayReturns]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `sale_returns_${today}`;

    if (type === "pdf") exportSaleReturnsToPDF(returns, `${base}.pdf`);
    handleClose();
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          Error fetching data: {error?.message || 'Unknown error'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          API URL: {process.env.REACT_APP_API_BASE_URL}/shop/sale-returns/
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Token: {token ? 'Present' : 'Missing'}
        </Typography>
        <RefreshIcon 
          sx={{ cursor: "pointer", verticalAlign: "middle", mt: 2 }} 
          onClick={() => window.location.reload()}
        />
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedSaleId}
                    onChange={(e) => setSelectedSaleId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_sale_ids")}</MenuItem>
                    {returns.map((ret, index) => (
                      <MenuItem key={index} value={ret.sale_details?.id?.toString() || ""}>
                        Sale #{ret.sale_details?.id || "N/A"}
                      </MenuItem>
                    )).filter((item, index, self) => 
                      index === self.findIndex(t => t.props.value === item.props.value)
                    )}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_customers")}</MenuItem>
                    {(customers || []).map((customer) => (
                      <MenuItem key={customer.id} value={customer.name}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedSaleId("");
                    setSelectedCustomer("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="sale returns table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.returnSale_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.returnSale_table_saleID", "Sale Reference")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_date", "Return Date")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_reason")}</TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_saleTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_refundTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_itemCount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayReturns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                  <Typography variant="h6" color="textSecondary">
                    {isLoading ? 'Loading...' : 'No return records found'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Total records from API: {returns.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Filtered records: {filteredReturns.length}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayReturns.map((ret) => (
                <Row key={ret.id} row={ret} />
              ))
            )}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalSaleAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRefundAmount.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("expense.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } } }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.ceil(finalCount / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        )}
      </Grid>
    </>
  );
}
